import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Input,
  Pagination,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import KCADashboardService from "../../../services/KCADashboardService";

const Dashboard = ({ userData }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedList, setSelectedList] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const pageSize = 5;

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await KCADashboardService.getGSMSummaryTotal();
      setSummary(res);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const data = summary?.data || {};

  const stats = [
    {
      title: "Today Active",
      pcs: data.today_active_pcs || 0,
      kanban: data.today_active_kanban_count || 0,
      list: data.today_active_kanban || [],
      color: "#2563eb",
      icon: <ClockCircleOutlined />,
    },
    {
      title: "Today Cleared",
      pcs: data.today_cleared_pcs || 0,
      kanban: data.today_cleared_kanban || 0,
      list: [],
      color: "#16a34a",
      icon: <CheckCircleOutlined />,
    },
    {
      title: "Overall Cleared",
      pcs: data.overall_cleared_pcs || 0,
      kanban: data.overall_cleared_kanban || 0,
      list: [],
      color: "#7c3aed",
      icon: <CheckCircleOutlined />,
    },
    {
      title: "Overall Active",
      pcs: data.overall_active_pcs || 0,
      kanban: data.overall_active_kanban_count || 0,
      list: data.overall_active_kanban || [],
      color: "#ea580c",
      icon: <AppstoreOutlined />,
    },
  ];

  const filteredList = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return selectedList;

    return selectedList.filter((item) =>
      String(item).toLowerCase().includes(q)
    );
  }, [selectedList, searchText]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredList.slice(start, start + pageSize);
  }, [filteredList, page]);

  const handleView = (title, list) => {
    setSelectedTitle(title);
    setSelectedList(Array.isArray(list) ? list : []);
    setSearchText("");
    setPage(1);
  };

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("Kanban number copied");
    } catch {
      message.error("Copy failed");
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(filteredList.join(", "));
      message.success("Kanban numbers copied");
    } catch {
      message.error("Copy failed");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "45px 12px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: 12, color: "#64748b" }}>
          Loading dashboard...
        </div>
      </div>
    );
  }

  if (!summary?.status) {
    return <Empty description="No dashboard data found" />;
  }

  return (
    <>
      <Row gutter={[16, 16]}>
        {stats.map((item) => (
          <Col xs={24} sm={12} xl={6} key={item.title}>
            <Card
              bordered={false}
              style={{
                height: "100%",
                borderRadius: 18,
                background: "#ffffff",
                boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
                border: "1px solid rgba(226, 232, 240, 0.9)",
              }}
              bodyStyle={{ padding: 18 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                <Typography.Text
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#334155",
                  }}
                >
                  {item.title}
                </Typography.Text>

                <div
                  style={{
                    minWidth: 42,
                    width: 42,
                    height: 42,
                    borderRadius: 14,
                    display: "grid",
                    placeItems: "center",
                    color: item.color,
                    background: `${item.color}14`,
                    fontSize: 20,
                  }}
                >
                  {item.icon}
                </div>
              </div>

              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  color: item.color,
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {item.pcs}
              </Typography.Title>

              <Typography.Text
                style={{
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Pieces
              </Typography.Text>

              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: "1px dashed #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                }}
              >
                <Typography.Text
                  style={{
                    color: "#64748b",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  Kanban
                </Typography.Text>

                <Typography.Title
                  level={4}
                  style={{
                    margin: 0,
                    color: "#0f172a",
                    fontWeight: 600,
                  }}
                >
                  {item.kanban}
                </Typography.Title>
              </div>

              {item.list.length > 0 && (
                <Button
                  block
                  icon={<EyeOutlined />}
                  onClick={() => handleView(item.title, item.list)}
                  style={{
                    marginTop: 16,
                    height: 40,
                    borderRadius: 12,
                    color: item.color,
                    borderColor: `${item.color}55`,
                    background: `${item.color}08`,
                    fontWeight: 700,
                    whiteSpace: "normal",
                  }}
                >
                  View Kanban Numbers
                </Button>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      {selectedTitle && (
        <Card
          bordered={false}
          style={{
            marginTop: 18,
            borderRadius: 18,
            background: "#ffffff",
            boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(226, 232, 240, 0.9)",
          }}
          bodyStyle={{ padding: 18 }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={10}>
              <Typography.Title
                level={4}
                style={{
                  margin: 0,
                  fontSize: 20,
                  lineHeight: 1.25,
                }}
              >
                {selectedTitle} Kanban Numbers
              </Typography.Title>
              <Typography.Text type="secondary">
                Total: {filteredList.length}
              </Typography.Text>
            </Col>

            <Col xs={24} md={14}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Search kanban number"
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setPage(1);
                  }}
                  style={{
                    width: "min(100%, 280px)",
                    height: 40,
                    borderRadius: 12,
                  }}
                />

                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyAll}
                  disabled={filteredList.length === 0}
                  style={{
                    height: 40,
                    borderRadius: 12,
                    fontWeight: 600,
                  }}
                >
                  Copy All
                </Button>
              </div>
            </Col>
          </Row>

          <div style={{ marginTop: 18 }}>
            {paginatedList.length === 0 ? (
              <Empty description="No kanban numbers found" />
            ) : (
              <Row gutter={[10, 10]}>
                {paginatedList.map((kanbanNo) => (
                  <Col xs={24} sm={12} lg={8} xl={6} key={kanbanNo}>
                    <div
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 14,
                        padding: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        background: "#f8fafc",
                        minWidth: 0,
                      }}
                    >
                    <Tag
  color="blue"
  style={{
    margin: 0,
    borderRadius: 999,
    fontWeight: 600,
    padding: "4px 9px",
    fontSize: "16px", // 👈 change this
    lineHeight: "16px", // optional for alignment
    maxWidth: "calc(100% - 78px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }}
>
  {kanbanNo}
</Tag>

                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopy(kanbanNo)}
                        style={{
                          borderRadius: 10,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </div>

          {filteredList.length > pageSize && (
            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "center",
                overflowX: "auto",
              }}
            >
              <Pagination
                size="small"
                current={page}
                pageSize={pageSize}
                total={filteredList.length}
                onChange={setPage}
                showSizeChanger={false}
              />
            </div>
          )}
        </Card>
      )}
    </>
  );
};

export default Dashboard;